// Considered: ContextFlowCanvasOrganism — rejected because this is an entry/navigation screen, not a flow editor. See spec Reuse Audit.
import React, { useState } from 'react';
import { useTranslation } from 'localization';
import './NewContextOrganism.scss';

type EntryPath = 'blank' | 'template' | 'ai';

interface Template {
  id: string;
  labelKey: string;
  descKey: string;
  accentColor: string;
  levels: string[];
  inwards?: boolean;
}

const TEMPLATES: Template[] = [
  {
    id: 'tmpl_pri',
    labelKey: 'context.newContext.templates.priorities.name',
    descKey: 'context.newContext.templates.priorities.desc',
    accentColor: 'var(--info-500)',
    levels: ['Pillars', 'Priorities', 'Strategies'],
  },
  {
    id: 'tmpl_rep',
    labelKey: 'context.newContext.templates.reporting.name',
    descKey: 'context.newContext.templates.reporting.desc',
    accentColor: 'var(--warning-500)',
    levels: ['Parent', 'Grandparent'],
    inwards: true,
  },
  {
    id: 'tmpl_team',
    labelKey: 'context.newContext.templates.team.name',
    descKey: 'context.newContext.templates.team.desc',
    accentColor: 'var(--purple-600)',
    levels: ['People', 'Tasks'],
  },
  {
    id: 'tmpl_okr',
    labelKey: 'context.newContext.templates.okr.name',
    descKey: 'context.newContext.templates.okr.desc',
    accentColor: 'var(--cyan-600)',
    levels: ['Objectives', 'Key Results', 'Initiatives'],
  },
];

export interface NewContextOrganismProps {
  onBlank: () => void;
  onTemplate: (templateId: string) => void;
  onCancel: () => void;
  cloningTemplateId: string | null;
  cloneError: string | null;
}

const NewContextOrganism: React.FC<NewContextOrganismProps> = ({
  onBlank,
  onTemplate,
  onCancel,
  cloningTemplateId,
  cloneError,
}) => {
  const { t } = useTranslation();
  const [activePath, setActivePath] = useState<EntryPath>('blank');

  return (
    <div className="new-context-organism" data-testid="new-context-organism">
      {/* Top bar */}
      <header className="new-context-organism__top-bar">
        <button type="button"
          className="new-context-organism__back-btn"
          onClick={onCancel}
          aria-label={t('context.newContext.topBar.backToLibrary')}
        >
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('context.newContext.topBar.backToLibrary')}</span>
        </button>
        <span className="new-context-organism__top-bar-sep" aria-hidden="true" />
        <div className="new-context-organism__title-group">
          <span className="new-context-organism__eyebrow">
            {t('context.newContext.topBar.eyebrow')}
          </span>
          <span className="new-context-organism__title">
            {t('context.newContext.topBar.untitled')}
          </span>
        </div>
        <span className="new-context-organism__spacer" />
          <button type="button"
          className="Save-button"
          disabled
          aria-label={t('context.newContext.topBar.saveDraft')}
        >
          <i className="pi pi-save mr-1" aria-hidden="true" />
          {t('context.newContext.topBar.saveDraft')}
        </button>
        <button type="button"
          className="Close-button"
          onClick={onCancel}
          data-testid="nc-cancel-btn"
        >
          {t('context.newContext.topBar.cancel')}
        </button>
      </header>

      {/* Scrollable body */}
      <div className="new-context-organism__body">
        <div className="new-context-organism__content">
          {/* Hero */}
          <div className="new-context-organism__hero">
            <span className="new-context-organism__hero-icon" aria-hidden="true">
              <i className="pi pi-database" />
            </span>
            <h1 className="new-context-organism__heading">
              {t('context.newContext.heading')}
            </h1>
            <p className="new-context-organism__description">
              {t('context.newContext.description')}
            </p>
          </div>

          {/* Path tiles */}
          <div className="new-context-organism__tiles" role="group" aria-label={t('context.newContext.heading')}>
            {/* AI tile — disabled */}
            <div className="new-context-organism__tile-wrap new-context-organism__tile-wrap--ai">
              <button type="button"
                className="new-context-organism__tile new-context-organism__tile--disabled"
                disabled
                data-testid="nc-ai-tile"
                aria-disabled="true"
              >
                <div className="new-context-organism__tile-header">
                  <span className="new-context-organism__tile-icon new-context-organism__tile-icon--ai">
                    <i className="pi pi-sparkles" aria-hidden="true" />
                  </span>
                  <span
                    className="new-context-organism__tile-badge new-context-organism__tile-badge--ai"
                    data-testid="nc-ai-coming-soon"
                  >
                    {t('context.newContext.tiles.ai.badge')}
                  </span>
                </div>
                <div className="new-context-organism__tile-title">
                  {t('context.newContext.tiles.ai.title')}
                </div>
                <div className="new-context-organism__tile-desc">
                  {t('context.newContext.tiles.ai.desc')}
                </div>
              </button>
            </div>

            {/* Blank tile */}
            <div
              className={`new-context-organism__tile-wrap ${activePath === 'blank' ? 'new-context-organism__tile-wrap--active-blank' : ''}`}
            >
              <button type="button"
                className={`new-context-organism__tile ${activePath === 'blank' ? 'new-context-organism__tile--active' : ''}`}
                onClick={() => setActivePath('blank')}
                data-testid="nc-blank-tile"
                aria-pressed={activePath === 'blank'}
              >
                <div className="new-context-organism__tile-header">
                  <span className="new-context-organism__tile-icon new-context-organism__tile-icon--blank">
                    <i className="pi pi-circle" aria-hidden="true" />
                  </span>
                </div>
                <div className="new-context-organism__tile-title">
                  {t('context.newContext.tiles.blank.title')}
                </div>
                <div className="new-context-organism__tile-desc">
                  {t('context.newContext.tiles.blank.desc')}
                </div>
              </button>
            </div>

            {/* Template tile */}
            <div
              className={`new-context-organism__tile-wrap ${activePath === 'template' ? 'new-context-organism__tile-wrap--active-template' : ''}`}
            >
              <button type="button"
                className={`new-context-organism__tile ${activePath === 'template' ? 'new-context-organism__tile--active' : ''}`}
                onClick={() => setActivePath('template')}
                data-testid="nc-template-tile"
                aria-pressed={activePath === 'template'}
              >
                <div className="new-context-organism__tile-header">
                  <span className="new-context-organism__tile-icon new-context-organism__tile-icon--template">
                    <i className="pi pi-table" aria-hidden="true" />
                  </span>
                </div>
                <div className="new-context-organism__tile-title">
                  {t('context.newContext.tiles.template.title')}
                </div>
                <div className="new-context-organism__tile-desc">
                  {t('context.newContext.tiles.template.desc')}
                </div>
              </button>
            </div>
          </div>

          {/* Active path body */}
          {activePath === 'blank' && (
            <div className="new-context-organism__blank-body" data-testid="nc-blank-body">
              <div className="new-context-organism__section-eyebrow">
                {t('context.newContext.blank.eyebrow')}
              </div>
              <div className="new-context-organism__blank-root">
                <span className="new-context-organism__blank-root-icon" aria-hidden="true">
                  <i className="pi pi-globe" />
                </span>
                <div className="new-context-organism__blank-root-text">
                  <span className="new-context-organism__blank-root-title">
                    {t('context.newContext.blank.rootTitle')}
                  </span>
                  <span className="new-context-organism__blank-root-desc">
                    {t('context.newContext.blank.rootDesc')}
                  </span>
                </div>
                <button type="button"
                  className="new-context-organism__open-editor-btn"
                  onClick={onBlank}
                  data-testid="nc-blank-open-editor"
                >
                  <i className="pi pi-arrow-right" aria-hidden="true" />
                  {t('context.newContext.blank.openEditor')}
                </button>
              </div>
              <div className="new-context-organism__blank-hint" role="note">
                <i className="pi pi-info-circle" aria-hidden="true" />
                <span>{t('context.newContext.blank.hint')}</span>
              </div>
            </div>
          )}

          {activePath === 'template' && (
            <div className="new-context-organism__template-body">
              <div className="new-context-organism__template-header">
                <span className="new-context-organism__section-eyebrow">
                  {t('context.newContext.template.eyebrow')}
                </span>
                <span className="new-context-organism__template-note">
                  {t('context.newContext.template.editableNote')}
                </span>
              </div>
              {cloneError && (
                <div className="new-context-organism__clone-error" role="alert" data-testid="nc-clone-error">
                  <i className="pi pi-exclamation-circle" aria-hidden="true" />
                  <span>{cloneError}</span>
                </div>
              )}
              <div className="new-context-organism__template-grid" data-testid="nc-template-grid">
                {TEMPLATES.map((tmpl) => (
                  <button type="button"
                    key={tmpl.id}
                    className="new-context-organism__template-card"
                    onClick={() => onTemplate(tmpl.id)}
                    disabled={cloningTemplateId !== null}
                    data-testid={`nc-tmpl-card-${tmpl.id}`}
                    aria-busy={cloningTemplateId === tmpl.id}
                  >
                    <div className="new-context-organism__template-card-header">
                      <span
                        className="new-context-organism__template-card-icon"
                        style={{ '--tmpl-accent': tmpl.accentColor } as React.CSSProperties}
                        aria-hidden="true"
                      >
                        {cloningTemplateId === tmpl.id ? (
                          <i className="pi pi-spin pi-spinner" />
                        ) : (
                          <i className="pi pi-database" />
                        )}
                      </span>
                      <span className="new-context-organism__template-card-name">
                        {t(tmpl.labelKey)}
                      </span>
                      <i className="pi pi-arrow-right new-context-organism__template-card-arrow" aria-hidden="true" />
                    </div>
                    <div className="new-context-organism__template-card-desc">
                      {t(tmpl.descKey)}
                    </div>
                    <div className="new-context-organism__template-card-levels">
                      <span className="new-context-organism__level-pill new-context-organism__level-pill--root">
                        root
                      </span>
                      {tmpl.levels.map((lv) => (
                        <React.Fragment key={lv}>
                          <i
                            className={`pi ${tmpl.inwards ? 'pi-arrow-left' : 'pi-arrow-right'} new-context-organism__level-arrow`}
                            aria-hidden="true"
                          />
                          <span
                            className="new-context-organism__level-pill"
                            style={{ '--tmpl-accent': tmpl.accentColor } as React.CSSProperties}
                          >
                            {lv}
                          </span>
                        </React.Fragment>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Closing handoff card */}
          <div className="new-context-organism__handoff">
            <span className="new-context-organism__handoff-icon" aria-hidden="true">
              <i className="pi pi-check" />
            </span>
            <div className="new-context-organism__handoff-text">
              <div className="new-context-organism__handoff-title">
                {t('context.newContext.handoff.title')}
              </div>
              <div className="new-context-organism__handoff-desc">
                {t('context.newContext.handoff.desc')}
              </div>
            </div>
            <button type="button" className="new-context-organism__handoff-open-btn" disabled>
              <i className="pi pi-play" aria-hidden="true" />
              {t('context.newContext.handoff.openIt')}
            </button>
            <button type="button"
              className="new-context-organism__handoff-view-btn"
              disabled
              data-testid="nc-handoff-create-view"
            >
              <i className="pi pi-th-large" aria-hidden="true" />
              {t('context.newContext.handoff.createView')}
            </button>
          </div>

          {/* Footer footnote */}
          <div className="new-context-organism__footnote" aria-hidden="true">
            <i className="pi pi-info-circle" />
            <span>{t('context.newContext.footer')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewContextOrganism;
